import { reactive } from 'vue'
import { apiFetch } from './api'

// ── Ordens de Serviço (grupos de itens/"rotinas" avulsas) ──
export interface ServiceOrderItem {
  id: string
  osId: string
  title: string
  description: string
  status: 'pending' | 'done' | 'approved'
  completedAt: string
  completedBy: string
  completedByName: string
  photoResponse: string
  textResponse: string
  checkResponse: boolean
  reviewedAt: string
  reviewedBy: string
  reviewedByName: string
  reviewNotes: string
  reworkCount: number
  createdAt: string
}

export interface ServiceOrder {
  id: string
  osNumber: number
  projectId: string
  projectName: string
  title: string
  description: string
  priority: 'baixa' | 'normal' | 'alta' | 'urgente'
  responsavelUserIds: string[]
  responsaveis: string[]
  dueDate: string
  status: 'pending' | 'approved' | 'cancelled'
  requireAll: boolean
  sourceOsId: string
  createdBy: string
  createdByName: string
  createdAt: string
  cancelledAt: string
  cancelReason: string
  itemsTotal: number
  itemsApproved: number
  hasPending: boolean
  lastCompletedAt: string
  lastCompletedByName: string
}

export interface ServiceOrderDetail extends ServiceOrder {
  items: ServiceOrderItem[]
}

// Item de OS atribuído ao usuário logado.
export interface MyServiceOrder {
  id: string
  osId: string
  osNumber?: number
  osTitle: string
  projectId: string
  projectName: string
  title: string
  description: string
  priority: 'baixa' | 'normal' | 'alta' | 'urgente'
  dueDate: string
  origin: 'service_order'
  status: 'pending' | 'completed' | 'cancelled'
}

export interface ServiceOrderExportRow {
  id: string
  osTitle: string
  itemTitle: string
  projectName: string
  priority: string
  status: string
  completedByName: string
  completedAt: string
  reviewedByName: string
  reviewedAt: string
  reviewNotes: string
  reworkCount: number
}

// ── Ordens de Serviço (grupos com 1+ responsáveis e N itens) ──
const serviceOrders = reactive<{ items: ServiceOrder[]; loading: boolean }>({ items: [], loading: false })

// Atalho do popup "+ Criar": sinaliza para MaintenanceServiceOrdersPanel abrir o
// formulário de nova OS automaticamente ao montar.
const state = reactive<{ pendingAction: null | 'create' }>({ pendingAction: null })

const loadServiceOrders = async (filters: Partial<{ projectId: string; status: string; search: string }> = {}) => {
  serviceOrders.loading = true
  try {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, String(value)) })
    const url = '/maintenance/service-orders' + (params.toString() ? '?' + params.toString() : '')
    serviceOrders.items = await apiFetch<ServiceOrder[]>(url)
  } finally {
    serviceOrders.loading = false
  }
}

const createServiceOrder = async (data: { projectId: string; projectName?: string; title: string; description?: string; priority?: string; dueDate?: string; responsavelUserIds: string[]; items: Array<{ title: string; description?: string }> }) => {
  const result = await apiFetch<{ id: string }>('/maintenance/service-orders', { method: 'POST', body: JSON.stringify(data) })
  await loadServiceOrders()
  return result.id
}

const loadServiceOrderDetail = async (id: string) => {
  return apiFetch<ServiceOrderDetail>(`/maintenance/service-orders/${encodeURIComponent(id)}`)
}

const cancelServiceOrder = async (id: string, reason?: string) => {
  await apiFetch(`/maintenance/service-orders/${encodeURIComponent(id)}/cancel`, { method: 'POST', body: JSON.stringify({ reason: reason || '' }) })
  await loadServiceOrders()
}

const deleteServiceOrder = async (id: string) => {
  await apiFetch(`/maintenance/service-orders/${encodeURIComponent(id)}`, { method: 'DELETE' })
  await loadServiceOrders()
}

const loadServiceOrderLineage = async (id: string) => {
  return apiFetch<ServiceOrder[]>(`/maintenance/service-orders/${encodeURIComponent(id)}/lineage`)
}

const addServiceOrderItem = async (osId: string, data: { title: string; description?: string }) => {
  const result = await apiFetch<{ id: string }>(`/maintenance/service-orders/${encodeURIComponent(osId)}/items`, { method: 'POST', body: JSON.stringify(data) })
  return result.id
}

const updateServiceOrder = async (id: string, data: { title?: string; description?: string; priority?: string; dueDate?: string }) => {
  await apiFetch(`/maintenance/service-orders/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(data) })
  await loadServiceOrders()
}

const updateServiceOrderItem = async (itemId: string, data: { title: string; description?: string }) => {
  await apiFetch(`/maintenance/service-order-items/${encodeURIComponent(itemId)}`, { method: 'PUT', body: JSON.stringify(data) })
}

const deleteServiceOrderItem = async (itemId: string) => {
  await apiFetch(`/maintenance/service-order-items/${encodeURIComponent(itemId)}`, { method: 'DELETE' })
}

const designateServiceOrder = async (osId: string, data: { responsavelUserIds: string[]; dueDate?: string; requireAll: boolean }) => {
  return apiFetch<{ ok: boolean; createdOsIds: string[]; osNumbers: number[] }>(
    `/maintenance/service-orders/${encodeURIComponent(osId)}/designate`,
    { method: 'POST', body: JSON.stringify(data) }
  )
}

const completeServiceOrderItem = async (itemId: string, data: { photoResponse?: string; textResponse?: string; checkResponse?: boolean }) => {
  await apiFetch(`/maintenance/service-order-items/${encodeURIComponent(itemId)}/complete`, { method: 'POST', body: JSON.stringify(data) })
}

const reviewServiceOrderItem = async (itemId: string, data: { approve: boolean; notes?: string }) => {
  await apiFetch(`/maintenance/service-order-items/${encodeURIComponent(itemId)}/review`, { method: 'POST', body: JSON.stringify(data) })
}

const fetchServiceOrdersExport = async (filters: Partial<{ projectId: string; status: string; dateFrom: string; dateTo: string }> = {}) => {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, String(value)) })
  const url = '/maintenance/service-orders-export' + (params.toString() ? '?' + params.toString() : '')
  return apiFetch<ServiceOrderExportRow[]>(url)
}

const myTasks = reactive<{ items: MyServiceOrder[]; loading: boolean }>({ items: [], loading: false })

const fetchMyTasks = async () => {
  myTasks.loading = true
  try {
    myTasks.items = await apiFetch<MyServiceOrder[]>('/maintenance/my-tasks')
  } finally {
    myTasks.loading = false
  }
}

export const maintenanceStore = {
  state,
  serviceOrders,
  loadServiceOrders,
  createServiceOrder,
  loadServiceOrderDetail,
  cancelServiceOrder,
  deleteServiceOrder,
  loadServiceOrderLineage,
  addServiceOrderItem,
  updateServiceOrder,
  updateServiceOrderItem,
  deleteServiceOrderItem,
  designateServiceOrder,
  completeServiceOrderItem,
  reviewServiceOrderItem,
  fetchServiceOrdersExport,
  myTasks,
  fetchMyTasks
}
